import React, { useEffect, useRef } from 'react';
import '../styles/Education.css';

// Using inline fallback variables for images, just in case imports are unavailable directly.
// The user provided paths in the prompt to the files: '../assets/faith.jpg', etc.
import faithImg from '../assets/faith.jpg';
import faithVid from '../assets/faithmemvid.mp4';
import malvarImg from '../assets/malvar.jpg';
import bulacninImg from '../assets/bulacnin.jpg';
import emiliaImg from '../assets/emilia.jpg';
import shsImg from '../assets/shs.png';
import jhsImg from '../assets/jhs.png';
import elemImg from '../assets/elem.png';

function Education() {
    const sectionRefs = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                    }
                });
            },
            { threshold: 0.15 }
        );

        sectionRefs.current.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            sectionRefs.current.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, []);

    const addToRefs = (el) => {
        if (el && !sectionRefs.current.includes(el)) {
            sectionRefs.current.push(el);
        }
    };

    return (
        <section id="education" className="education-section">
            <div className="edu-container">
                <div className="edu-header fade-up" ref={addToRefs}>
                    <h2 className="section-title">Education Journey</h2>
                </div>

                {/* CURRENT EDUCATION CARD */}
                <div className="current-edu-card fade-up" ref={addToRefs}
                    onMouseEnter={e => e.currentTarget.querySelector('video').play()}
                    onMouseLeave={e => { const v = e.currentTarget.querySelector('video'); v.pause(); v.currentTime = 0; }}
                >
                    <div className="current-hover-vid-overlay">
                        <video src={faithVid} muted loop playsInline />
                    </div>
                    <div className="current-img-wrapper">
                        <img src={faithImg} alt="FAITH Colleges" />
                    </div>
                    <div className="current-info">
                        <span className="current-badge">✨ Current</span>
                        <h3 className="current-school">FAITH Colleges</h3>
                        <p className="current-program">Bachelor of Science in Computer Engineering</p>
                        <p className="current-details">
                            📍 2 Pres. JP Laurel Highway, Darasa, Tanauan City, Batangas
                        </p>
                        <p className="current-details">📅 2023 – Present</p>
                        <p className="current-details">🎓 3rd Year</p>
                        <p className="current-description" style={{ marginTop: '15px', color: 'var(--ozone-dark)', opacity: 0.9, lineHeight: 1.6, fontStyle: 'italic', borderLeft: '3px solid var(--glaze)', paddingLeft: '15px', background: 'rgba(254, 255, 175, 0.1)', padding: '15px', borderRadius: '0 12px 12px 0' }}>
                            Currently pursuing a Bachelor of Science in Computer Engineering, focusing on developing skills in hardware-software integration, embedded systems, and innovative technology solutions.
                        </p>
                    </div>
                </div>

                {/* PREVIOUS SCHOOLS TIMELINE */}
                <div className="timeline-section">
                    <h3 className="fade-up" ref={addToRefs}>Previous Schools</h3>
                    <div className="timeline">
                        {/* Timeline Item 1 */}
                        <div className="timeline-item left fade-up" ref={addToRefs}>
                            <div className="timeline-content" data-hover-img={malvarImg}>
                                <div className="timeline-hover-img">
                                    <img src={shsImg} alt="Malvar Senior High School hover" />
                                </div>
                                <span className="timeline-year">2021 – 2023</span>
                                <div className="timeline-img">
                                    <img src={malvarImg} alt="Malvar Senior High School" />
                                </div>
                                <h4 className="timeline-school">Malvar Senior High School</h4>
                                <p className="timeline-level">Senior High School • STEM Strand</p>
                                <p className="timeline-address">📍 Poblacion, Malvar, Batangas</p>
                            </div>
                            <div className="timeline-desc">
                                Completed the STEM strand, building a strong foundation in science, mathematics, and problem-solving that prepared me for engineering studies.
                            </div>
                        </div>

                        {/* Timeline Item 2 */}
                        <div className="timeline-item right fade-up" ref={addToRefs}>
                            <div className="timeline-content" data-hover-img={bulacninImg}>
                                <div className="timeline-hover-img">
                                    <img src={jhsImg} alt="Bulacnin hover" />
                                </div>
                                <span className="timeline-year">2017 – 2021</span>
                                <div className="timeline-img">
                                    <img src={bulacninImg} alt="Bulacnin Integrated National High School" />
                                </div>
                                <h4 className="timeline-school">Bulacnin Integrated National High School</h4>
                                <p className="timeline-level">Junior High School</p>
                                <p className="timeline-address">📍 Bulacnin, Lipa City</p>
                            </div>
                            <div className="timeline-desc">
                                Completed junior high school with consistent academic performance, developing discipline, teamwork, and fundamental academic skills.
                            </div>
                        </div>

                        {/* Timeline Item 3 */}
                        <div className="timeline-item left fade-up" ref={addToRefs}>
                            <div className="timeline-content" data-hover-img={emiliaImg}>
                                <div className="timeline-hover-img">
                                    <img src={elemImg} alt="Emilia hover" />
                                </div>
                                <span className="timeline-year">2011 – 2017</span>
                                <div className="timeline-img">
                                    <img src={emiliaImg} alt="Emilia L. Malabanan Integrated School" />
                                </div>
                                <h4 className="timeline-school">Emilia L. Malabanan Integrated School</h4>
                                <p className="timeline-level">Primary School</p>
                                <p className="timeline-address">📍 Sitio Sampalocan, Malabanan, Balete, Batangas</p>
                            </div>
                            <div className="timeline-desc">
                                Completed primary education as a consistent honor student and graduated as Class Valedictorian, demonstrating early academic excellence and dedication.
                            </div>
                        </div>
                    </div>
                </div>

                {/* ACHIEVEMENTS SECTION */}
                <div className="achievements-section fade-up" ref={addToRefs}>
                    <h3>Achievements</h3>
                    <div className="achievements-grid">
                        <div className="achievement-card">
                            <div className="achievement-icon">⭐</div>
                            <h4 className="achievement-title">K-12 Consistent Honor</h4>
                        </div>
                        <div className="achievement-card">
                            <div className="achievement-icon">🏅</div>
                            <h4 className="achievement-title">Grade 6 Class Valedictorian</h4>
                        </div>
                        <div className="achievement-card">
                            <div className="achievement-icon">🏆</div>
                            <h4 className="achievement-title">1st to 2nd Year Dean’s Lister</h4>
                        </div>
                    </div>
                </div>

                {/* ACADEMIC FOCUS SECTION */}
                <div className="focus-section fade-up" ref={addToRefs}>
                    <h3>Academic Focus & Interests</h3>
                    <div className="focus-grid">
                        <div className="focus-card">
                            <h4>📘 Academic Focus</h4>
                            <ul>
                                <li>Embedded Systems</li>
                                <li>IoT Systems</li>
                                <li>Hardware-Software Integration</li>
                            </ul>
                        </div>
                        <div className="focus-card">
                            <h4>📚 Relevant Coursework</h4>
                            <ul>
                                <li>Data Structures and Algorithms</li>
                                <li>Digital Systems Design</li>
                                <li>Microprocessors</li>
                                <li>Computer Networks</li>
                            </ul>
                        </div>
                        <div className="focus-card">
                            <h4>💡 Specialization Interests</h4>
                            <ul>
                                <li>IoT-based Healthcare Systems</li>
                                <li>Smart Embedded Devices</li>
                                <li>Machine Learning Integration in Hardware</li>
                            </ul>
                        </div>
                    </div>
                </div>
                
            </div>
        </section>
    );
}

export default Education;

